import React, { FC, useState } from "react";
import { FaFilter } from "react-icons/fa";
import Select from "react-select";
import { createOptionListForSelectTag } from "../../../helpers/utils/Utils";
import { selectStyle } from "../../../helpers/utils/StyleUtils";
import SkeletonLoader from "../../../components/common/SkeletonLoader";
import { useGetOrderLogListQuery } from "../productsApi";
import { useGetEnumsQuery } from "../../../app/globalApi";
import AuditableInfo from "../../../components/common/AuditableInfo";
import { skipToken } from "@reduxjs/toolkit/query";
import { useParams } from "react-router-dom";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import { useResolveErrorByIdMutation } from "../../sysAdmin/error/errorLogsApi";

interface props {
    productId: string
}

interface HistoryRowProps {
    data: any;
}

const OrderHistory: FC<props> = ({ productId }) => {

    const [selectedLogType, setSelectedLogType] = useState<string[]>(['INFO', 'WARN', 'ERROR'])
    const [show, setShow] = useState(false)
    const { data: enums } = useGetEnumsQuery("")
    const { orderId } = useParams();
    const [showResolveVisibilityConfirmation, setShowResolveVisibilityConfirmation] = useState(false);
    const [orderLogId, setOrderLogId] = useState<any>();
    const [resolveErrorLogs, { isLoading: resolveErrorLogApiResponse }] = useResolveErrorByIdMutation();

    const {
        data: orderLogs,
        isLoading: loading,
        isError: error,
    } = useGetOrderLogListQuery(orderId ? {
        productId: orderId,
        logTypes: selectedLogType
    } : skipToken);


    const resolveOrderLog = async (e: any) => {
        e.preventDefault();
        if (orderId && orderLogId) {
            await resolveErrorLogs({ orderId: orderId, orderLogId: orderLogId }).unwrap();
            setShowResolveVisibilityConfirmation(false);
        }

    }

    const Row: FC<HistoryRowProps> = ({ data }) => (
        <div title={data?.logType}
            className={`p-3 lg:grid sm:block grid-cols-12 border border-surface-5 pb-2 border-2 border-[rgba(0,0,0,0.1)]rounded bg-text-0`}>

            <div className="col-span-6">
                <div className="flex gap-2">
                    {data?.logType && data?.logType?.toUpperCase() === 'INFO' && (
                        <div
                            className="text-text-0 bg-highlight-4 p-1 text-[8px] font-bold leading-none rounded"
                        >
                            {data.logType}
                        </div>
                    )}
                    {data?.logType?.toUpperCase() === 'ERROR' && (
                        <>
                            <div className="text-text-0 bg-error-1 p-1 text-[8px] font-bold leading-none rounded">
                                {data.logType}
                            </div>
                            <div className="text-text-0 bg-[#5F9EA0] px-1 text-[8px] font-bold  rounded">
                                {hasPermission(["ERRLOG-U"]) && (
                                    <>
                                        <span
                                            className="cursor-pointer"
                                            onClick={(e) => {
                                                setOrderLogId(data?.id);
                                                setShowResolveVisibilityConfirmation(true);
                                            }}
                                        >
                                            Resolve
                                        </span>
                                        <br />
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    {data?.logType && data?.logType?.toUpperCase() === 'DEBUG' && (
                        <div
                            className="text-text-0 bg-[#2BA2B8] p-1 text-[8px] font-bold leading-none rounded"
                        >
                            {data.logType}
                        </div>
                    )}
                    {data?.logType && data?.logType?.toUpperCase() === 'WARN' && (
                        <div
                            className="bg-[#ffc107] p-1 text-[8px] font-bold leading-none rounded"
                        >
                            {data.logType}
                        </div>
                    )}

                </div>
                <div title={data?.detailedLogMessage ?? data?.logMessage} className="text-surface-0">
                    {data?.logMessage}
                </div>
            </div>
            <div className="col-span-6 text-sm flex flex-col items-end text-text-2">

                <AuditableInfo data={data} />


            </div>




        </div>
    );

    const handleChange = (selectedOption: any[]) => {
        const selectedOptions = selectedOption?.map(c => c.value) || []
        setSelectedLogType(selectedOptions)
    };

    const iconButtonStyle = "cursor-pointer text-highlight-0 text-md hover:text-highlight-7";

    return (
        <>
            <div className="bg-text-1 shadow-[0_3px_10px_#0003] text-sm mt-3 rounded-md">
                <div className="flex justify-between items-center border-b border-[rgba(0,0,0,0.1)] p-2">
                    <div className="text-surface-0 font-semibold flex gap-3">
                        <h3 className="font-semibold">Order History ({orderLogs?.length || 0}) </h3>
                        <div className={"mt-1"}>
                            <FaFilter className={iconButtonStyle} onClick={() => {
                                setShow(!show)
                            }} />
                        </div>
                    </div>
                </div>
                <div className={'p-2'}>
                    {
                        show && <Select
                            placeholder='Select filter options...'
                            isMulti={true}
                            options={createOptionListForSelectTag(enums?.LogType, "value", "constant")}
                            value={createOptionListForSelectTag(enums?.LogType, "value", "constant").filter((option: any) => selectedLogType.includes(option.value))}
                            //@ts-ignore
                            onChange={handleChange}
                            styles={selectStyle}
                        />
                    }
                    <div className='overflow-y-auto max-h-56'>
                        {
                            (loading) ?
                                <SkeletonLoader className={"mt-2"} count={3}></SkeletonLoader> :
                                !error && orderLogs?.length === 0 ?
                                    <div className="mt-2 border-2  text-surface-0 bg-text-0  border-surface-5 p-4 rounded-sm">
                                        <h3 >
                                            No order history to display
                                        </h3>
                                    </div> :
                                    !error && orderLogs?.map((data: any) => {
                                        return <Row
                                            data={data}
                                        />
                                    })
                        }
                    </div>
                </div>
            </div>
            {
                showResolveVisibilityConfirmation && <ConfirmationModal title={"Resolve Error"} bodyMessage={"Are you sure you want to resolve this error..."}
                    onSubmit={resolveOrderLog}
                    show={showResolveVisibilityConfirmation}
                    setShow={setShowResolveVisibilityConfirmation}
                    isLoading={resolveErrorLogApiResponse}
                />
            }
        </>
    )
}

export default OrderHistory;